import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, set } from 'firebase/database';
import { auth, db } from '../firebase';
import './Support.css';

function Support({ currentUser }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    category: '',
    description: '',
    mobile: '',
    email: '',
    links: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.displayName || prev.name,
        email: currentUser.email || prev.email,
      }));
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'subject' && value.length > 200) return;
    if (name === 'description' && value.length > 5000) return;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Generate short random ID like TKT-X8A9
      const randomStr = Math.random().toString(36).substring(2, 6).toUpperCase();
      const ticketId = `TKT-${randomStr}`;
      
      const payload = {
        ...formData,
        uid: currentUser?.uid || 'anonymous',
        status: 'open',
        timestamp: Date.now(),
      };

      await set(ref(db, `tickets/${ticketId}`), payload);
      
      alert(`Ticket ${ticketId} created successfully! Our support team will get back to you.`);
      navigate('/chat');
    } catch (error) {
      console.error(error);
      alert('Failed to create ticket. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-container">
      <div className="support-content">
        <div className="support-header">
          <h1>How can we help?</h1>
          <p>Browse our FAQ or contact our support team directly.</p>
          <p>Tell us how we can help</p>
        </div>

        <div className="support-form-card">
          <h2>New Support Ticket</h2>
          <p>Please provide details about your issue. We'll get back to you as soon as possible.</p>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Name <span className="required-asterisk">*</span></label>
              <input type="text" name="name" className="support-input" value={formData.name} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>
                <span>Subject <span className="required-asterisk">*</span></span>
                <span className="char-counter">{formData.subject.length}/200</span>
              </label>
              <input type="text" name="subject" className="support-input" value={formData.subject} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Category <span className="required-asterisk">*</span></label>
              <select name="category" className="support-input" value={formData.category} onChange={handleChange} required>
                <option value="" disabled>Select a category</option>
                <option value="Account">Account Access</option>
                <option value="Billing">Billing & Subscriptions</option>
                <option value="Bug">Report a Bug</option>
                <option value="Feature">Feature Request</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <span>Description <span className="required-asterisk">*</span></span>
                <span className="char-counter">{formData.description.length}/5000</span>
              </label>
              <textarea name="description" className="support-input" placeholder="Provide detailed information about your issue..." value={formData.description} onChange={handleChange} required></textarea>
            </div>

            <div className="form-group">
              <label>Mobile Number <span className="required-asterisk">*</span></label>
              <div className="phone-input-wrapper">
                <span className="phone-prefix">+91</span>
                <input type="tel" name="mobile" className="support-input phone-input" value={formData.mobile} onChange={handleChange} required />
              </div>
            </div>

            <div className="form-group">
              <label>Email <span className="required-asterisk">*</span></label>
              <input type="email" name="email" className="support-input" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label>Attach Files (Optional)</label>
              <div className="file-input-wrapper">
                <button type="button" className="file-btn">Choose File</button>
                <span style={{ fontSize: '0.9rem', color: '#9ca3af' }}>No files chosen</span>
              </div>
              <div className="file-hint">Max 5MB per file. Total limit: 25MB</div>
            </div>

            <div className="form-group">
              <label>Relevant Links (Optional)</label>
              <input type="url" name="links" className="support-input" placeholder="https://example.com" value={formData.links} onChange={handleChange} />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => navigate(-1)} disabled={loading}>Cancel</button>
              <button type="submit" className="btn-submit" disabled={loading}>{loading ? 'Submitting...' : 'Create a New Ticket'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Support;
